import { ChangeEvent } from "react";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import { useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import {
  FieldsetCard,
  getDisabledClasses,
} from "~/common/components/ui/FieldsetCard";
import Input from "~/common/components/ui/Input";
import Select from "~/common/components/ui/select/Select";
import { NOT_AVAILABLE } from "~/common/constants";
import {
  RELATIONSHIPS,
  relationshipsOptions,
} from "~/patients/constants/relationships";
import { useFormatHook } from "~/patients/hooks/useFormatHook";
import { AccountSchemaType } from "~/patients/utils/PatientSchemas";

interface EmergencyContactFieldsetProps {
  disabled?: boolean;
}

export const EmergencyContactFieldset = ({
  disabled,
}: EmergencyContactFieldsetProps) => {
  const {
    register,
    control,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<AccountSchemaType>();
  const { formatPhoneNumber } = useFormatHook();

  const otherRelationship =
    watch("emergency_contacts.0.relationship") === RELATIONSHIPS.OTHER;

  return (
    <FieldsetCard
      title="Emergency Contact"
      disabled={disabled}
      className="border-none p-0 shadow-none"
      contentClassName={twMerge(
        "grid-cols-2 sm:grid-cols-4",
        !disabled && "sm:grid-cols-2",
      )}
    >
      <Input
        id="emergency_contact_name"
        label="First name"
        {...register("emergency_contacts.0.name")}
        {...getDisabledClasses(disabled)}
        error={errors?.name?.message}
      />
      <Input
        id="emergency_contact_last_name"
        label="Last name"
        {...register("emergency_contacts.0.last_name")}
        {...getDisabledClasses(disabled)}
        error={errors?.last_name?.message}
      />

      <Select
        error={errors?.emergency_contacts?.[0]?.relationship?.message}
        label="Relationship"
        placeholder={disabled ? NOT_AVAILABLE : "Select relationship"}
        id="emergency_contacts.0.relationship"
        options={relationshipsOptions}
        control={control}
        containerClassName="gap-2"
        isDisabled={disabled}
        buttonClassName="disabled:bg-transparent disabled:border-none disabled:p-0 disabled:h-auto"
        icon={disabled ? <ChevronDownIcon /> : undefined}
      />
      {otherRelationship && (
        <Input
          id="customRelationship"
          label="Custom relationship"
          placeholder="Enter relationship"
          {...register("emergency_contacts.0.customRelationship")}
          {...getDisabledClasses(disabled)}
          error={errors?.emergency_contacts?.[0]?.customRelationship?.message}
        />
      )}

      <Input
        id="emergency_contact_mobile_number"
        label="Mobile number"
        {...register("emergency_contacts.0.phone_number", {
          onChange: (event: ChangeEvent<HTMLInputElement>) => {
            setValue(
              "emergency_contacts.0.phone_number",
              formatPhoneNumber(event.target.value),
            );
          },
        })}
        {...getDisabledClasses(disabled)}
        error={errors?.emergency_contacts?.[0]?.phone_number?.message}
      />

      <Input
        id="emergency_contact_email"
        label="Email"
        {...register("emergency_contacts.0.email")}
        {...getDisabledClasses(disabled)}
        error={errors?.emergency_contacts?.[0]?.email?.message}
      />
    </FieldsetCard>
  );
};
